import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { useUpdateWorkPackage } from '../../hooks/useCapexQueries'
import { RemediatesSelect } from './RemediatesSelect'
import type { WorkPackage } from './types'

interface EditWorkPackageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  pkg: WorkPackage | null
  org: string
  buildingAssetUid?: string | null
}

export function EditWorkPackageDialog({
  open,
  onOpenChange,
  pkg,
  org,
  buildingAssetUid,
}: EditWorkPackageDialogProps) {
  const { t } = useTranslation('capex')

  const [label, setLabel] = useState('')
  const [costLow, setCostLow] = useState('')
  const [costHigh, setCostHigh] = useState('')
  const [remediates, setRemediates] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Pre-fill fields from pkg prop
  useEffect(() => {
    if (pkg) {
      setLabel(pkg.label ?? '')
      setCostLow(pkg.cost_low != null ? String(pkg.cost_low) : '')
      setCostHigh(pkg.cost_high != null ? String(pkg.cost_high) : '')
      setRemediates(pkg.compliance_evaluation_uid ?? '')
      setErrors({})
    }
  }, [pkg])

  const updateMutation = useUpdateWorkPackage(org)

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!label.trim()) {
      newErrors.label = t('create_package_dialog.error_label_required')
    }

    const low = Number(costLow)
    const high = Number(costHigh)

    if (costLow === '' || isNaN(low) || low < 0) {
      newErrors.cost_low = t('create_package_dialog.error_cost_low_invalid')
    }

    if (costHigh === '' || isNaN(high) || high < 0) {
      newErrors.cost_high = t('create_package_dialog.error_cost_high_invalid')
    } else if (!newErrors.cost_low && high < low) {
      newErrors.cost_high = t('create_package_dialog.error_cost_high_below_low')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate() || !pkg) return
    try {
      await updateMutation.mutateAsync({
        packageUid: pkg.uid!,
        body: {
          label: label.trim(),
          cost_low: Number(costLow),
          cost_high: Number(costHigh),
          ...(remediates ? { compliance_evaluation_uid: remediates } : {}),
        },
      })
      onOpenChange(false)
    } catch {
      // error handled by mutation state
    }
  }

  const handleClose = () => {
    setErrors({})
    onOpenChange(false)
  }

  if (!pkg) return null

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose() }}>
      <DialogContent data-testid="edit-package-dialog">
        <DialogHeader>
          <DialogTitle>{t('edit_package_dialog.title', 'Edit work package')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Label */}
          <div className="space-y-1.5">
            <Label htmlFor="edit-pkg-label">{t('create_package_dialog.field_label')}</Label>
            <Input
              id="edit-pkg-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t('create_package_dialog.field_label_placeholder')}
              data-testid="edit-package-label"
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            />
            {errors.label && <p className="text-sm text-destructive">{errors.label}</p>}
          </div>

          {/* Cost range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="edit-pkg-cost-low">{t('create_package_dialog.field_cost_low')}</Label>
              <Input
                id="edit-pkg-cost-low"
                type="number"
                min={0}
                value={costLow}
                onChange={(e) => setCostLow(e.target.value)}
                placeholder="0"
                data-testid="edit-package-cost-low"
              />
              {errors.cost_low && <p className="text-sm text-destructive">{errors.cost_low}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-pkg-cost-high">{t('create_package_dialog.field_cost_high')}</Label>
              <Input
                id="edit-pkg-cost-high"
                type="number"
                min={0}
                value={costHigh}
                onChange={(e) => setCostHigh(e.target.value)}
                placeholder="0"
                data-testid="edit-package-cost-high"
              />
              {errors.cost_high && <p className="text-sm text-destructive">{errors.cost_high}</p>}
            </div>
          </div>

          <RemediatesSelect
            org={org}
            buildingAssetUid={buildingAssetUid}
            value={remediates}
            onChange={setRemediates}
            id="edit-pkg-remediates"
            testId="edit-package-remediates-select"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t('create_package_dialog.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={updateMutation.isPending}
            data-testid="edit-package-submit"
          >
            {updateMutation.isPending
              ? t('create_package_dialog.submitting')
              : t('edit_package_dialog.submit', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
